import { useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery } from 'urql';

import { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { DiscoverInstagramCreatorsQuery } from '@frontend/gql/social/graphql';

import { QueryVariables } from '../../types';
import { discoverInstagramCreators } from '../graphql';

export const useInstagramCreatorSearchData = (
  queryVariables: QueryVariables,
  clientId: string,
  selectedInstagramAccount?: CreatorDiscoveryInstagramAccount,
  defaultFiltersResolved?: boolean,
) => {
  const [searchResults, setSearchResults] = useState<DiscoverInstagramCreatorsQuery | null>(null);
  const [refetching, setRefetching] = useState(false);
  const [isPaginationFetching, setIsPaginationFetching] = useState(false);
  const [paginationKey, setPaginationKey] = useState(0);
  const { igUserId } = selectedInstagramAccount ?? {};

  // Variables for discoverInstagramCreators query
  const discoverVariables = useMemo(() => {
    const { creatorUsername: _creatorUsername, prompt: _prompt, ...graphqlVariables } = queryVariables;
    return {
      ...graphqlVariables,
      igUserId: igUserId || '',
      clientId,
      // Force fresh queries for pagination by including a key that changes
      _paginationKey: paginationKey,
    };
  }, [queryVariables, igUserId, clientId, paginationKey]);

  // Main search query
  const [{ data: discoverData, fetching: discoverFetching, error: discoverError }, refetchDiscover] = useQuery({
    query: discoverInstagramCreators,
    variables: discoverVariables,
    pause: !igUserId || defaultFiltersResolved === false,
  });

  const fetching = discoverFetching;
  const error = discoverError;
  const data = discoverData;

  useEffect(() => {
    if (!fetching) {
      setRefetching(false);
      setIsPaginationFetching(false);
    }
  }, [fetching]);

  // Process query results
  useEffect(() => {
    if (discoverData) {
      setSearchResults(discoverData);
    } else {
      setSearchResults(null);
    }
  }, [discoverData]);

  // Handle search submission
  const handleRefresh = useCallback(() => {
    setRefetching(true);
    refetchDiscover({ requestPolicy: 'network-only' });
  }, [refetchDiscover]);

  // Handle pagination
  const handlePagination = useCallback(() => {
    setIsPaginationFetching(true);
    // Force fresh query execution by updating the pagination key
    setPaginationKey((prev) => prev + 1);
  }, []);

  return {
    data,
    fetching: fetching || defaultFiltersResolved === false, // Show loading while waiting for default filters
    error,
    searchResults,
    handleSearch: handleRefresh,
    handlePagination,
    refetch: refetchDiscover,
    refetching,
    isPaginationFetching,
  };
};
